import { useState, useEffect, useRef, useMemo } from 'react';
import { Check, X, Plus } from 'lucide-react';
import { createPortal } from 'react-dom';
import type { CustomField, CustomFieldValue } from '@/types';
import { fetchCustomFieldsForContext } from '@/db/customFields';
import { getProjectIdByPath as getSpaceIdByPath } from '@/db/projects';
import { InlineDatePicker } from '@/components/shared/inline-editors';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useDropdownPosition } from '@/hooks/useDropdownPosition';
import { handleError } from '@/utils/errorHandler';
import clsx from 'clsx';

// ─── Multi-select dropdown (matches project style) ──────────────────────────

function MultiSelectDropdown({
  value,
  onChange,
  options,
  disabled = false,
}: {
  value: string[];
  onChange: (values: string[]) => void;
  options: Array<{ value: string; label: string; color?: string }>;
  disabled?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownStyle = useDropdownPosition(isOpen, triggerRef, {
    offset: 8,
    minWidth: 200,
    matchTriggerWidth: false,
  });

  useClickOutside([dropdownRef, triggerRef], () => { setIsOpen(false); setQuery(''); }, isOpen);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const toggle = (optValue: string) => {
    const next = value.includes(optValue)
      ? value.filter(v => v !== optValue)
      : [...value, optValue];
    onChange(next);
  };

  const MAX_DISPLAY = 2;

  return (
    <>
      <div
        ref={triggerRef}
        className="relative inline-flex items-center gap-1 rounded-md overflow-hidden h-6"
      >
        <div className="flex items-center gap-1 min-w-0 overflow-hidden">
          {value.length === 0 ? (
            <span className="text-[10px] text-text-quaternary flex items-center gap-1 shrink-0">
              {disabled ? '—' : ''}
            </span>
          ) : (
            <>
              {value.slice(0, MAX_DISPLAY).map((v) => {
                const label = options.find(o => o.value === v)?.label || v;
                return (
                  <span
                    key={v}
                    className={clsx(
                      'inline-flex items-center gap-1 rounded bg-accent-violet/10 text-accent-violet font-medium group shrink-0 h-4 min-w-0 max-w-[80px] px-1.5 text-[9px]',
                      !disabled && 'cursor-pointer hover:bg-accent-violet/20'
                    )}
                    onClick={(e) => { e.stopPropagation(); if (!disabled) toggle(v); }}
                    title={disabled ? label : `Rimuovi "${label}"`}
                  >
                    <span className="truncate min-w-0 flex-1">{label}</span>
                    {!disabled && (
                      <X className="w-2.5 h-2.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </span>
                );
              })}
              {value.length > MAX_DISPLAY && (
                <span className="text-[9px] text-text-quaternary font-medium shrink-0 px-1">
                  +{value.length - MAX_DISPLAY}
                </span>
              )}
            </>
          )}
        </div>
        {!disabled && (
          <button
            onClick={(e) => { e.stopPropagation(); setIsOpen(prev => !prev); }}
            className={clsx(
              'inline-flex items-center justify-center rounded hover:bg-surface-hover text-text-quaternary hover:text-accent-violet transition-all shrink-0 w-4 h-4',
              value.length === 0 && 'ml-0.5'
            )}
            title="Aggiungi"
          >
            <Plus className="w-3 h-3" />
          </button>
        )}
      </div>
      {isOpen && dropdownStyle && createPortal(
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className="rounded-lg shadow-2xl min-w-[220px] max-w-[280px] animate-fade-in border border-border-primary/70 bg-surface-primary overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="p-2 border-b border-border-primary/50">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca..."
              className="w-full px-2.5 py-1.5 text-[11px] bg-surface-tertiary border border-border-primary/50 rounded-md focus:outline-none focus:ring-1 focus:ring-accent-violet focus:border-accent-violet text-text-primary placeholder-text-quaternary transition-all"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-[240px] overflow-y-auto">
            {/* Selected */}
            {value.length > 0 && (
              <div className="px-2 pt-2 pb-1">
                <div className="px-2 py-1 text-[10px] font-semibold text-text-quaternary uppercase tracking-wider">
                  Selezionati ({value.length})
                </div>
                <div className="space-y-0.5">
                  {value.filter(v => {
                    const label = options.find(o => o.value === v)?.label || v;
                    return !query || label.toLowerCase().includes(query.toLowerCase());
                  }).map((v) => {
                    const label = options.find(o => o.value === v)?.label || v;
                    return (
                      <button
                        key={v}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(v); }}
                        className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md hover:bg-accent-violet/10 transition-colors group"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent-violet shrink-0" />
                          <span className="text-[11px] text-text-primary truncate">{label}</span>
                        </div>
                        <X className="w-3 h-3 text-text-quaternary group-hover:text-accent-rose shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Available */}
            {filtered.filter(o => !value.includes(o.value)).length > 0 && (
              <div className={clsx('px-2 pb-2', value.length > 0 && 'pt-2 border-t border-border-primary/30')}>
                {value.length > 0 && (
                  <div className="px-2 py-1 text-[10px] font-semibold text-text-quaternary uppercase tracking-wider">
                    Disponibili
                  </div>
                )}
                <div className="space-y-0.5">
                  {filtered.filter(o => !value.includes(o.value)).map((opt) => (
                    <button
                      key={opt.value}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(opt.value); }}
                      className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-white/5 transition-colors"
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-text-quaternary/40 shrink-0" />
                      <span className="text-[11px] text-text-primary truncate">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-center">
                <div className="text-[11px] text-text-quaternary">Nessun risultato</div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// ─── Single-select dropdown (tag-pill style) ────────────────────────────────

function SingleSelectDropdown({
  value,
  onChange,
  options,
  disabled = false,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
  options: Array<{ value: string; label: string; color?: string }>;
  disabled?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownStyle = useDropdownPosition(isOpen, triggerRef, {
    offset: 8,
    minWidth: 200,
    matchTriggerWidth: false,
  });

  useClickOutside([dropdownRef, triggerRef], () => { setIsOpen(false); setQuery(''); }, isOpen);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const selectedLabel = options.find(o => o.value === value)?.label;

  return (
    <>
      <div
        ref={triggerRef}
        className="relative inline-flex items-center gap-1 rounded-md overflow-hidden h-6"
      >
        <div className="flex items-center gap-1 min-w-0 overflow-hidden">
          {!value || !selectedLabel ? (
            <span className="text-[10px] text-text-quaternary shrink-0">
              {disabled ? '—' : ''}
            </span>
          ) : (
            <span
              className={clsx(
                'inline-flex items-center gap-1 rounded bg-accent-violet/10 text-accent-violet font-medium group shrink-0 h-4 min-w-0 max-w-[100px] px-1.5 text-[9px]',
                !disabled && 'cursor-pointer hover:bg-accent-violet/20'
              )}
              onClick={(e) => { e.stopPropagation(); if (!disabled) onChange(null); }}
              title={disabled ? selectedLabel : `Rimuovi "${selectedLabel}"`}
            >
              <span className="truncate min-w-0 flex-1">{selectedLabel}</span>
              {!disabled && (
                <X className="w-2.5 h-2.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
              )}
            </span>
          )}
        </div>
        {!disabled && (
          <button
            onClick={(e) => { e.stopPropagation(); setIsOpen(prev => !prev); }}
            className={clsx(
              'inline-flex items-center justify-center rounded hover:bg-surface-hover text-text-quaternary hover:text-accent-violet transition-all shrink-0 w-4 h-4',
              !value && 'ml-0.5'
            )}
            title="Seleziona"
          >
            <Plus className="w-3 h-3" />
          </button>
        )}
      </div>
      {isOpen && dropdownStyle && createPortal(
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className="rounded-lg shadow-2xl min-w-[220px] max-w-[280px] animate-fade-in border border-border-primary/70 bg-surface-primary overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="p-2 border-b border-border-primary/50">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca..."
              className="w-full px-2.5 py-1.5 text-[11px] bg-surface-tertiary border border-border-primary/50 rounded-md focus:outline-none focus:ring-1 focus:ring-accent-violet focus:border-accent-violet text-text-primary placeholder-text-quaternary transition-all"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-[240px] overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center">
                <div className="text-[11px] text-text-quaternary">Nessun risultato</div>
              </div>
            ) : (
              <div className="px-2 space-y-0.5">
                {filtered.map((opt) => {
                  const selected = opt.value === value;
                  return (
                    <button
                      key={opt.value}
                      onClick={(e) => {
                        e.preventDefault(); e.stopPropagation();
                        onChange(selected ? null : opt.value);
                        setIsOpen(false); setQuery('');
                      }}
                      className={clsx(
                        'w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-colors',
                        selected ? 'bg-accent-violet/10 hover:bg-accent-violet/10' : 'hover:bg-white/5'
                      )}
                    >
                      <div className={clsx(
                        'w-1.5 h-1.5 rounded-full shrink-0',
                        selected ? 'bg-accent-violet' : 'bg-text-quaternary/40'
                      )} />
                      <span className="text-[11px] text-text-primary truncate">{opt.label}</span>
                      {selected && <Check className="w-3 h-3 text-accent-violet shrink-0 ml-auto" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

interface CustomFieldEditorProps {
  field: CustomField;
  value: CustomFieldValue;
  onChange: (value: CustomFieldValue) => void;
  disabled?: boolean;
}

export function CustomFieldEditor({ field, value, onChange, disabled }: CustomFieldEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.type === 'text') inputRef.current.select();
    }
  }, [isEditing]);

  // Defensive: ensure field is valid
  if (!field || !field.type) {
    return null;
  }

  if (field.type === 'text') {
    const textVal = (value as string) || '';
    if (isEditing && !disabled) {
      return (
        <input
          ref={inputRef}
          type="text"
          value={textVal}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setIsEditing(false)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setIsEditing(false); }}
          className="w-full px-1.5 h-6 rounded-md bg-surface-tertiary text-[11px] text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-violet"
          onClick={(e) => e.stopPropagation()}
        />
      );
    }
    return (
      <span
        onClick={(e) => { e.stopPropagation(); if (!disabled) setIsEditing(true); }}
        className={clsx(
          'text-[11px] truncate px-1.5 h-6 flex items-center rounded-md',
          textVal ? 'text-text-secondary' : 'text-text-quaternary/50',
          !disabled && 'cursor-pointer hover:bg-surface-tertiary'
        )}
      >
        {textVal || '—'}
      </span>
    );
  }

  if (field.type === 'number') {
    const numVal = typeof value === 'number' ? value : (value !== null && value !== undefined && value !== '' ? Number(value) : null);
    const displayVal = numVal !== null && !isNaN(numVal) ? String(numVal) : '';
    if (isEditing && !disabled) {
      return (
        <input
          ref={inputRef}
          type="number"
          value={displayVal}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === '' ? null : Number(v));
          }}
          onBlur={() => setIsEditing(false)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setIsEditing(false); }}
          className="w-full px-1.5 h-6 rounded-md bg-surface-tertiary text-[11px] text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-violet [&::-webkit-inner-spin-button]:appearance-none"
          onClick={(e) => e.stopPropagation()}
        />
      );
    }
    return (
      <span
        onClick={(e) => { e.stopPropagation(); if (!disabled) setIsEditing(true); }}
        className={clsx(
          'text-[11px] truncate px-1.5 h-6 flex items-center rounded-md',
          displayVal ? 'text-text-secondary' : 'text-text-quaternary/50',
          !disabled && 'cursor-pointer hover:bg-surface-tertiary'
        )}
      >
        {displayVal || '—'}
      </span>
    );
  }

  if (field.type === 'boolean') {
    return (
      <div className="flex items-center justify-center h-6">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => { e.stopPropagation(); onChange(e.target.checked); }}
          disabled={disabled}
          onClick={(e) => e.stopPropagation()}
          className="w-3.5 h-3.5 rounded border-border-primary text-accent-violet focus:ring-accent-violet cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>
    );
  }

  if (field.type === 'date') {
    const msValue =
      value instanceof Date ? value.getTime() : typeof value === 'number' ? value : null;

    return (
      <InlineDatePicker
        value={msValue}
        onChange={(ms) => onChange(ms !== null ? new Date(ms) : null)}
        label="—"
        showTime={false}
        size="sm"
        disabled={disabled}
      />
    );
  }

  if (field.type === 'dropdown') {
    const rawOptions = field.options || [];
    const options = Array.isArray(rawOptions) ? rawOptions : [];
    const isMulti = field.icon === 'multi';

    if (!isMulti) {
      // Single select — tag-pill style with dropdown
      return (
        <SingleSelectDropdown
          value={(value as string) || null}
          onChange={(v) => onChange(v)}
          options={options}
          disabled={disabled}
        />
      );
    }

    // Multi select
    return (
      <MultiSelectDropdown
        value={Array.isArray(value) ? value : value ? [value as string] : []}
        onChange={(vals) => onChange(vals.length > 0 ? vals : null)}
        options={options}
        disabled={disabled}
      />
    );
  }

  return null;
}

interface CustomFieldsSectionProps {
  task: { spaceId?: string; spaceName?: string; folderPath?: string[]; listName?: string; path?: string };
  customFields?: Record<string, CustomFieldValue> | Record<string, unknown>;
  onCustomFieldChange: (fieldId: string, value: CustomFieldValue) => void;
  disabled?: boolean;
}

// Simple cache for custom fields to avoid redundant loads
const customFieldsCache = new Map<string, { fields: CustomField[]; timestamp: number }>();
const CACHE_TTL = 60000; // 1 minute

export function CustomFieldsSection({
  task,
  customFields = {},
  onCustomFieldChange,
  disabled = false,
}: CustomFieldsSectionProps) {
  const [fields, setFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadFields = async () => {
      setLoading(true);
      try {
        // Use spaceId directly if available (most reliable)
        let containerId: string | undefined = task.spaceId || undefined;

        // Fallback: resolve from path or spaceName
        if (!containerId) {
          if (task.path) {
            containerId = (await getSpaceIdByPath(task.path)) || undefined;
          } else if (task.spaceName) {
            const pathParts: string[] = [task.spaceName];
            if (task.folderPath && task.folderPath.length > 0 && task.folderPath[0]) {
              pathParts.push(task.folderPath[0]);
            }
            if (task.listName) {
              pathParts.push(task.listName);
            }
            containerId = (await getSpaceIdByPath(pathParts.join('/'))) || undefined;
          }
        }

        if (!containerId) {
          if (!cancelled) { setFields([]); setLoading(false); }
          return;
        }

        const cacheKey = `cf_${containerId}`;
        const cached = customFieldsCache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
          if (!cancelled) { setFields(cached.fields); setLoading(false); }
          return;
        }

        const fetchedFields = await fetchCustomFieldsForContext(containerId, undefined, containerId);
        if (!cancelled) {
          setFields(fetchedFields);
          customFieldsCache.set(cacheKey, { fields: fetchedFields, timestamp: Date.now() });
        }
      } catch (err) {
        handleError(err, { level: 'warning', message: 'Errore nel caricamento dei campi personalizzati' });
        if (!cancelled) setFields([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadFields();
    return () => { cancelled = true; };
  }, [task.spaceId, task.spaceName, task.folderPath, task.listName, task.path]);

  if (loading) {
    return null;
  }

  if (fields.length === 0) {
    return null;
  }

  return (
    <div className="px-4 py-4 border-t border-border-primary">
      <h3 className="text-[10px] uppercase tracking-wider text-text-quaternary mb-3 font-medium">
        Campi personalizzati
      </h3>
      <div className="space-y-3">
        {fields.map((field) => (
          <div key={field.id}>
            <label className="block text-[10px] font-medium text-text-secondary mb-1.5">
              {field.name}
            </label>
            <CustomFieldEditor
              field={field}
              value={customFields[field.id] as CustomFieldValue}
              onChange={(value) => onCustomFieldChange(field.id, value)}
              disabled={disabled}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
